import { Link } from "react-router-dom";
import { ArrowRight, Shield, Gavel, Scroll, Building2, CheckCircle2, Phone, Quote, Star, Sparkles } from "lucide-react";
import { WHATSAPP_URL } from "../lib/api";
import SEO from "../components/SEO";

const practiceAreas = [
  {
    icon: Shield,
    title: "Anticipatory Bail",
    desc: "Pre-arrest protection before PHHC and Sessions Courts under Section 482 BNSS / 438 CrPC.",
    to: "/services/anticipatory-bail",
  },
  {
    icon: Gavel,
    title: "Regular & Interim Bail",
    desc: "Swift bail applications post-arrest, with custody hearings handled end-to-end.",
    to: "/services/bail-lawyer",
  },
  {
    icon: Scroll,
    title: "Corporate Defence",
    desc: "FIR quashing, writ petitions (corporate), 482 CrPC, 498A, NDPS, and trial representation.",
    to: "/services/corporate-lawyer",
  },
];

const stats = [
  { k: "2+", v: "Years at the Bar" },
  { k: "5+", v: "Matters argued" },
  { k: "PHHC", v: "Principal Court" },
  { k: "24 hrs", v: "Response time" },
];

const testimonials = [
  {
    quote:
      "Advocate Shubham Makkar secured my anticipatory bail within 48 hours. Clear communication, zero drama, pure competence.",
    name: "R. Sharma",
    case: "Anticipatory Bail — PHHC",
    rating: 5,
  },
  {
    quote:
      "We were terrified after the FIR. He broke down every step and argued fearlessly. Our family owes him.",
    name: "A. Kaur",
    case: "Corporate Miscellaneous — CJM Chandigarh",
    rating: 5,
  },
  {
    quote:
      "Genuine counsel, not a salesman. He told me when I had a case and when I did not. Rare integrity.",
    name: "V. Singh",
    case: "Writ Petition (Corporate) — PHHC",
    rating: 5,
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO
        title="Advocate Shubham Makkar — Corporate Lawyer in Chandigarh | PHHC Bail Lawyer"
        description="Top corporate lawyer in Chandigarh. Anticipatory bail, regular bail and corporate defence at Punjab &amp; Haryana High Court and Chandigarh District Courts. Immediate consultation."
        keywords="corporate lawyer chandigarh, bail lawyer chandigarh, anticipatory bail chandigarh, advocate chandigarh court, phhc bail lawyer"
      />

      {/* ---------- Hero ---------- */}
      <section className="relative pt-[72px] overflow-hidden bg-gradient-to-b from-[#EFF6FF] via-white to-white">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div
          className="absolute -top-32 -right-40 w-[700px] h-[700px] rounded-full opacity-30 blur-3xl"
          style={{ background: "radial-gradient(circle, #93C5FD 0%, transparent 70%)" }}
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7 fade-up">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("open-chatbot"))}
              className="group inline-flex items-center gap-2 bg-white border border-blue-100 shadow-sm hover:shadow-md hover:border-[#1D4ED8]/40 px-4 py-2 rounded-full mb-8 transition-all cursor-pointer"
              data-testid="hero-free-ai-help-btn"
            >
              <Sparkles size={14} className="text-[#1D4ED8] group-hover:scale-110 transition-transform" />
              <span className="text-xs tracking-wider text-[#1D4ED8] font-medium">
                Free AI Help · Chat now
              </span>
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 ml-1 animate-pulse"></span>
            </button>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#0A2540]">
              Building the <em className="gradient-text not-italic">legal architecture</em>
              <br></br>
              <span className="text-slate-700">for global leaders. </span>
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-2xl leading-relaxed">
              I am a corporate lawyer practising at the Punjab &amp; Haryana
              High Court and Chandigarh courts. I translate complex business goals into legally sound structures, ensuring that
              every deal is as commercially viable as it is legally complaint. With a background spanning BCOM, PGDIB, LLB, LLM, I offer
              a rare fusion of business and legal mastery.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/book" className="primary-btn" data-testid="hero-book-btn">
                Book a Paid Consultation <ArrowRight size={16} />
              </Link>
              <a
                href={WHATSAPP_URL("Namaste Advocate Shubham Makkar, I need urgent help.")}
                target="_blank"
                rel="noopener noreferrer"
                className="ghost-btn"
                data-testid="hero-whatsapp-btn"
              >
                <Phone size={16} /> WhatsApp Now
              </a>
            </div>

            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
              {stats.map((s) => (
                <div key={s.v} className="border-l-2 border-[#1D4ED8] pl-4">
                  <div className="font-serif text-3xl text-[#0A2540] font-semibold">{s.k}</div>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mt-1 font-medium">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative">
              {/* floating card top */}
              <div className="absolute -top-6 -left-6 z-10 bg-white shadow-xl rounded-xl px-5 py-4 border border-slate-100 float-anim">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={18} className="text-emerald-500" />
                  <div>
                    <div className="text-xs text-slate-500">Latest Win</div>
                    <div className="text-sm font-semibold text-[#0A2540]">Anticipatory Bail Granted</div>
                  </div>
                </div>
              </div>
              {/* floating card bottom */}
              <div className="absolute -bottom-6 -right-6 z-10 bg-white shadow-xl rounded-xl px-5 py-4 border border-slate-100 float-anim" style={{ animationDelay: "1.2s" }}>
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-1">
                    {[0, 1, 2].map(i => (
                      <div key={i} className="w-7 h-7 rounded-full border-2 border-white bg-gradient-to-br from-[#1D4ED8] to-[#0A2540] text-white text-[10px] flex items-center justify-center font-semibold">
                        {["R", "A", "V"][i]}
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">350+ clients</div>
                    <div className="text-sm font-semibold text-[#0A2540] flex items-center gap-1">
                      4.9 <Star size={12} className="text-amber-400" fill="currentColor" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-xl shadow-blue-900/10 ring-1 ring-slate-200/70">
                <img
                  src="https://images.pexels.com/photos/5668484/pexels-photo-5668484.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=850&w=700"
                  alt="Gavel"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {/* soft top fade to blend with light page bg */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(239,246,255,0.85) 0%, rgba(239,246,255,0.15) 18%, transparent 40%, rgba(10,37,64,0.55) 80%, rgba(10,37,64,0.92) 100%)",
                  }}
                />
                <div className="absolute left-6 right-6 bottom-6 text-white">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-blue-200 mb-2 font-semibold">
                    Building Legal Architecture since 2025
                  </div>
                  <div className="font-serif text-2xl leading-tight">UG-4, Green Lotus Saksham, Zirakpur, Mohali</div>
                  <div className="text-sm text-blue-100 mt-1">
                    Appearance across Punjab &amp; Haryana
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Practice areas ---------- */}
      <section className="py-24 bg-white" data-testid="practice-areas">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-3">
            <span className="eyebrow">Practice</span>
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-[#0A2540] max-w-3xl">
            A focused practice. <span className="italic text-[#1D4ED8]">Argued at the right forum</span>, at the right time.
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {practiceAreas.map((p, idx) => (
              <Link
                key={p.title}
                to={p.to}
                className="group card-elevated p-8 lg:p-10 block"
                data-testid={`practice-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#EFF6FF] to-[#DBEAFE] flex items-center justify-center rounded-xl mb-6 group-hover:from-[#1D4ED8] group-hover:to-[#0A2540] transition-all duration-300">
                  <p.icon size={22} className="text-[#1D4ED8] group-hover:text-white transition-colors" />
                </div>
                <div className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-2 font-semibold">
                  0{idx + 1}
                </div>
                <h3 className="font-serif text-2xl text-[#0A2540] mb-3 font-semibold">{p.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                <div className="mt-8 text-sm text-[#1D4ED8] inline-flex items-center gap-2 font-medium">
                  Learn more <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Why choose ---------- */}
      <section className="py-24 tint-band">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <div className="mb-3">
              <span className="eyebrow">The Counsel</span>
            </div>
            <h2 className="font-serif text-4xl lg:text-5xl text-[#0A2540]">
              Counsel you call <em className="text-[#1D4ED8] not-italic">once</em>  and trust for life.
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed text-lg">
              Corporate law is not paperwork. It is reputation, liberty, and often, survival.
              Advocate Shubham Makkar runs a deliberately small practice so every brief gets the
              preparation, presence, and post-hearing follow-through it deserves.
            </p>
            <Link to="/book" className="primary-btn mt-10" data-testid="why-book-btn">
              Book a Consultation <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              ["Courts of Appearance", "PHHC, Sessions, CJM Chandigarh, district courts across Punjab & Haryana."],
              ["Urgent Matters", "Anticipatory bail drafted and moved within 24–48 hours where grounds permit."],
              ["Transparent Fees", "No surprises. Fee schedule shared in writing at the consultation stage."],
              ["Discretion", "Every client matter is held in the strictest confidence. Always."],
            ].map(([t, d]) => (
              <div key={t} className="card-elevated p-8">
                <div className="w-10 h-10 bg-[#DBEAFE] rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle2 size={18} className="text-[#1D4ED8]" />
                </div>
                <div className="font-serif text-xl text-[#0A2540] mb-2 font-semibold">{t}</div>
                <div className="text-sm text-slate-600 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Courts strip ---------- */}
      <section className="py-14 bg-[#0A2540] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#93C5FD] mb-6 text-center">
            Appears Before
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {[
              "Punjab & Haryana High Court",
              "Sessions Court Chandigarh",
              "CJM Chandigarh",
              "District Courts · Mohali",
              "District Courts · Batala",
            ].map((c) => (
              <div key={c} className="flex items-center gap-2 text-blue-100 text-sm">
                <Building2 size={16} className="text-[#93C5FD]" />
                {c}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- Testimonials ---------- */}
      <section className="py-24 bg-white" data-testid="testimonials">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-3">
            <span className="eyebrow">Voices</span>
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-[#0A2540] max-w-3xl">
            What clients say after court.
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <figure key={i} className="card-elevated p-8 lg:p-10 relative">
                <Quote size={32} className="text-[#DBEAFE] absolute top-6 right-6" />
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} size={14} className="text-amber-400" fill="currentColor" />
                  ))}
                </div>
                <blockquote className="font-serif italic text-xl text-slate-700 leading-snug">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-6 pt-5 border-t border-slate-100">
                  <div className="text-[#0A2540] text-sm font-semibold">{t.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{t.case}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- CTA strip ---------- */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="relative bg-gradient-to-br from-[#0A2540] via-[#1E3A8A] to-[#1D4ED8] rounded-3xl p-12 lg:p-16 text-center overflow-hidden">
            <div className="absolute inset-0 dot-grid opacity-20" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#93C5FD] mb-4">
                Every hour counts
              </div>
              <h2 className="font-serif text-4xl lg:text-5xl text-white">
                Facing an FIR, arrest or custody?
              </h2>
              <p className="mt-6 text-blue-100 max-w-2xl mx-auto text-lg">
                Send a message now. We respond within one business hour, often sooner.
              </p>
              <div className="mt-10 flex flex-wrap justify-center gap-4">
                <Link
                  to="/book"
                  className="bg-white text-[#0A2540] hover:bg-blue-50 px-8 py-3.5 rounded-full font-semibold inline-flex items-center gap-2 transition-all shadow-lg hover:-translate-y-0.5"
                  data-testid="cta-book"
                >
                  Book Consultation <ArrowRight size={16} />
                </Link>
                <a
                  href={WHATSAPP_URL("Urgent legal help required.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/10 backdrop-blur border border-white/30 text-white hover:bg-white/20 px-8 py-3.5 rounded-full font-semibold inline-flex items-center gap-2 transition-all"
                  data-testid="cta-whatsapp"
                >
                  <Phone size={16} /> Message on WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
